import { z } from 'zod';

/**
 * The `sonicforge-visual-review` format (Phase 5C.1): the strict schema and its limits,
 * with no dependencies beyond Zod so tests and tools can validate a review.json on their
 * own. The builder is in `document.ts`.
 */

/** Most frames one review may contain. */
export const MAX_REVIEW_FRAMES = 8;

export const REVIEW_FORMAT = 'sonicforge-visual-review';
export const REVIEW_VERSION = 1;
export const MAX_REVIEW_DOCUMENT_BYTES = 256 * 1024;
export const MAX_BRIEF_CHARS = 1000;

export const REVIEW_LIMITATIONS = [
  'Each frame is a single instant. Frames cannot show smoothness, easing, timing feel, flicker or anything that happens between them.',
  'Frame-to-frame differences show positions at the listed times only; per-frame object values are what was drawn at that instant.',
  'Framing is always the whole scene: SonicForge has no camera moves or zoom in project data yet.',
  'No audio is included. Audio metadata, when present, is derived from the project music (event times) and its analysis (levels 0–1), not from hearing it.',
  'Frames come from the video-export renderer at full effect quality; on-screen previews can use draft effects and differ slightly at edges.',
  'Colours are sRGB PNG as rendered by this browser’s WebGL; another device can differ by a few levels.',
] as const;

const Num = z.number().finite();
const Hex = z.string().regex(/^#[0-9a-f]{6}$/i);
const Id = z.string().min(1).max(64);
const Name = z.string().max(80);
const Values = z.record(z.string().max(32), Num);

const FrameSchema = z.strictObject({
  id: z.string().regex(/^f0[1-8]$/),
  /** The PNG file next to this document: `review-<first 8 of reviewId>-frame-0N.png`. */
  file: z.string().regex(/^review-[0-9a-f]{8}-frame-0[1-8]\.png$/),
  seconds: Num.min(0),
  /** Grid frame index of `seconds` at `timeline.fps`. */
  gridFrame: z.number().int().min(0),
  /** Animation frame drawn (open-ended counter when the session has no animation). */
  animationFrame: z.number().int().min(0),
  bytes: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/).optional(),
  /** Drawn values of animated or reacting objects (object id → channel → value). */
  objects: z.record(Id, Values),
  effects: z.strictObject({ flash: Num.min(0).max(1), backgroundPulse: Num.min(0).max(1), shake: z.tuple([Num, Num]) }).nullable(),
  audio: z
    .strictObject({
      nearbyEvents: z.array(z.strictObject({ category: z.string().max(32), offsetSec: Num, velocity: Num.min(0).max(1) })).max(8),
      levels: z.strictObject({ rms: Num, low: Num, mid: Num, high: Num }).nullable(),
    })
    .nullable(),
});

const LookSchema = z.record(z.string(), z.unknown());

const ObjectSchema = z.strictObject({
  id: Id,
  name: Name.optional(),
  type: z.enum(['rect', 'circle', 'ellipse', 'polygon', 'text', 'image', 'character']),
  layerId: Id,
  box: z.tuple([Num, Num, Num, Num]),
  rotation: Num,
  opacity: Num,
  visible: z.boolean(),
  fill: Hex.optional(),
  stroke: z.strictObject({ color: Hex, width: Num }).nullable().optional(),
  gradient: LookSchema.nullable().optional(),
  glow: LookSchema.nullable().optional(),
  shadow: LookSchema.nullable().optional(),
  cornerRadius: Num.optional(),
  text: z.string().max(500).optional(),
  textStyle: z.strictObject({ fontSize: Num, color: Hex, bold: z.boolean(), align: z.string(), fontFamily: z.string() }).optional(),
  characterId: Id.optional(),
  flipX: z.boolean().optional(),
  imageAssetId: Id.optional(),
  fit: z.string().optional(),
  animatedChannels: z.array(z.string().max(32)),
});

export const VisualReviewSchema = z.strictObject({
  format: z.literal(REVIEW_FORMAT),
  version: z.literal(REVIEW_VERSION),
  reviewId: z.string().regex(/^vr_[0-9a-f]{16}$/),
  createdAt: z.string().datetime(),
  app: z.strictObject({ name: z.literal('SonicForge Studio'), projectSchemaVersion: z.number().int(), creationPackageVersion: z.number().int() }),
  target: z.strictObject({
    projectId: Id,
    /** Fingerprint of the project's creative content; a revision package must target it. */
    contextRevision: z.string().regex(/^r[0-9a-f]{16}$/),
    sceneId: Id,
    sceneName: Name.optional(),
    source: z.discriminatedUnion('kind', [
      z.strictObject({ kind: z.literal('sync-session'), sessionId: Id, animationId: Id.nullable() }),
      z.strictObject({ kind: z.literal('animation'), animationId: Id }),
    ]),
  }),
  brief: z.strictObject({ goal: z.string().max(MAX_BRIEF_CHARS), constraints: z.string().max(MAX_BRIEF_CHARS) }).nullable(),
  render: z.strictObject({
    renderer: z.literal('video-export'),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    sceneWidth: z.number().int().positive(),
    sceneHeight: z.number().int().positive(),
    /** Output pixels per scene pixel. */
    scale: Num.positive(),
    pixelRatio: z.literal(1),
    effectsQuality: z.literal('full'),
    framing: z.literal('full-scene'),
    camera: z.null(),
    format: z.literal('png'),
    colorSpace: z.literal('srgb'),
  }),
  timeline: z.strictObject({
    durationSec: Num.positive(),
    /** Frame grid the timestamps sit on. */
    fps: z.number().int().positive(),
    loop: z.string().max(16),
    selection: z.enum(['even', 'custom']),
  }),
  frames: z.array(FrameSchema).min(1).max(MAX_REVIEW_FRAMES),
  inventory: z.strictObject({
    scene: z.strictObject({
      width: z.number().int(),
      height: z.number().int(),
      background: Hex,
      backgroundGradient: z.strictObject({ to: Hex, angle: Num }).nullable(),
      hasBackgroundImage: z.boolean(),
      look: LookSchema.nullable(),
    }),
    layers: z.array(z.strictObject({ id: Id, name: Name.optional(), visible: z.boolean(), opacity: Num, blend: z.string(), objectCount: z.number().int() })),
    objects: z.array(ObjectSchema).max(500),
    characters: z.array(z.strictObject({ id: Id, name: Name.optional(), appearance: LookSchema })),
    animation: z
      .strictObject({ id: Id, name: Name.optional(), fps: z.number().int(), durationFrames: z.number().int(), seconds: Num, loop: z.string(), tracks: z.array(z.strictObject({ objectId: Id, channel: z.string(), keyframes: z.number().int() })) })
      .nullable(),
    reactions: z.array(z.strictObject({ id: Id, reaction: z.string(), categories: z.array(z.string()), objectId: Id.nullable(), enabled: z.boolean() })),
    visualizer: z.strictObject({ layers: z.number().int(), types: z.array(z.string()) }).nullable(),
  }),
  audio: z
    .strictObject({
      bpm: Num,
      durationSec: Num,
      eventCounts: z.record(z.string(), z.number().int()),
      analysis: z.enum(['measured', 'not-available']),
    })
    .nullable(),
  limitations: z.array(z.string()).min(1),
  privacy: z.strictObject({ namesIncluded: z.boolean(), excluded: z.array(z.string()) }),
});
export type VisualReview = z.infer<typeof VisualReviewSchema>;
